import cx from 'classnames'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  MiniMap,
  type ReactZoomPanPinchRef,
  TransformComponent,
  TransformWrapper,
} from 'react-zoom-pan-pinch'
import type { PlateDimensions } from '~/api/backend/commonTypes'
import type { PlatePage_PlateImagingObservation } from '~/api/backend/pages/platePageAPI'
import {
  isWellImagingObservation,
  type WellPage_WellCulture,
} from '~/api/backend/pages/wellPageAPI'
import ExpandIcon from '~/components/icons/ExpandIcon'
import { useFeatureFlag } from '~/core/featureFlags'
import { useLargeImageBlobUrls, useStitchedMontageImage } from '~/hooks/montage-image'
import { prefersLargeMontageSprite } from '~/pages/plate/plateSpriteSizing'
import { warmImageURL } from '~/utils/images'
import { defaultMontageLayout, MONTAGE_LAYOUT_ROWS_COLS } from '~/utils/montage'
import {
  ExpandedMontageModal,
  useServerStitchedImage,
  type ZoomPanState,
} from './ExpandedMontageModal'
import {
  DEFAULT_FILTERS,
  generateFilterString,
  type ImageFilters,
} from './ImageManipulationControls'
import cs from './montage_hero.scss'

export default function MontageHero(props: {
  culture: WellPage_WellCulture
  observationTimestamp?: string
  onClickImage?: (imageIndex: number) => void
  zoomPanState?: ZoomPanState
  onZoomPanChange?: (state: ZoomPanState) => void
  stitchImage?: boolean
  imageFilters?: ImageFilters
  onLoadingStateChange?: (isLoading: boolean) => void
  onViewportDimensionsChange?: (dimensions: { width: number; height: number }) => void
}) {
  const onZoomPanChange = props.onZoomPanChange
  const onLoadingStateChange = props.onLoadingStateChange
  const onViewportDimensionsChange = props.onViewportDimensionsChange
  const stitchImage = props.stitchImage
  const panAndZoom = useFeatureFlag('panAndZoom') ?? false
  const culture = props.culture
  const imageFilters = props.imageFilters ?? DEFAULT_FILTERS
  const containerRef = useRef<HTMLDivElement>(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const [viewportDimensions, setViewportDimensions] = useState<{
    width: number
    height: number
  } | null>(null)

  const wellObservation = culture.observationHistory
    .filter(isWellImagingObservation)
    .find(obs => obs.timestamp === props.observationTimestamp)
  const plateObservation = culture.culturePlate.plateObservationHistory.find(
    obs => obs.timestamp === props.observationTimestamp,
  ) as PlatePage_PlateImagingObservation | undefined

  const montageLayout =
    plateObservation?.sprite?.montageLayout ??
    // This default is needed for the case where the well hasn't been imaged
    // yet. We can revisit this if we change the design so that a non-imaged
    // well doesn't show a placeholder layout.
    defaultMontageLayout(culture.culturePlate.plateDimensions)
  const [montageRows, montageColumns] = MONTAGE_LAYOUT_ROWS_COLS[montageLayout]
  const numSitesInMontageLayout = montageRows * montageColumns

  // V2 well sprite URL (bounded dimensions)
  // Use large sprite (3000px) for high effective viewport sizes, standard (1500px) otherwise
  // Well sprites have much higher per-site resolution than plate sprites
  // (e.g., 1000px/site for 3x3 vs ~31px/site for 96-well plate)
  const preferLargeSprite = prefersLargeMontageSprite({
    zoomEnabled: panAndZoom,
  })
  const v2WellSpriteUrl = preferLargeSprite
    ? (wellObservation?.montage?.stitchedImageUrlLarge ??
      wellObservation?.montage?.stitchedImageUrlStandard)
    : wellObservation?.montage?.stitchedImageUrlStandard
  const wellSpriteURL = v2WellSpriteUrl ? warmImageURL(v2WellSpriteUrl) : null

  // Large sprite URL for expanded modal (always use highest resolution)
  const largeSpriteUrl = wellObservation?.montage?.stitchedImageUrlLarge
    ? warmImageURL(wellObservation.montage.stitchedImageUrlLarge)
    : null

  const singleImageURL = wellObservation?.montage?.images[0]?.imageUrl
    ? warmImageURL(wellObservation.montage.images[0].imageUrl)
    : null

  // Use well sprite for both grid view and stitched view (higher quality than plate sprite)
  const rawServerSpriteUrlV2 = wellSpriteURL

  // V1 well sprite URL (legacy - unbounded dimensions)
  // Used as fallback during v2 backfill period
  const rawServerSpriteUrlV1 = wellObservation?.montage?.stitchedImageUrl
    ? warmImageURL(wellObservation.montage.stitchedImageUrl)
    : null

  // Try v2 first, then fall back to v1
  const {
    data: validatedServerSpriteUrlV2,
    isLoading: isValidatingV2,
    error: serverSpriteErrorV2,
  } = useServerStitchedImage(rawServerSpriteUrlV2, panAndZoom)

  // Only try v1 if v2 failed or doesn't exist
  const shouldTryV1 =
    panAndZoom &&
    !validatedServerSpriteUrlV2 &&
    !isValidatingV2 &&
    !!rawServerSpriteUrlV1
  const {
    data: validatedServerSpriteUrlV1,
    isLoading: isValidatingV1,
    error: serverSpriteErrorV1,
  } = useServerStitchedImage(rawServerSpriteUrlV1, shouldTryV1)

  // Use v2 if available, otherwise v1
  const validatedServerSpriteUrl =
    validatedServerSpriteUrlV2 ?? validatedServerSpriteUrlV1
  const isValidatingServerSprite = isValidatingV2 || (shouldTryV1 && isValidatingV1)
  const serverSpriteError = validatedServerSpriteUrlV2
    ? null
    : serverSpriteErrorV2 && serverSpriteErrorV1

  // Server sprite is available if validated successfully
  const serverSpriteAvailable = !!validatedServerSpriteUrl && !serverSpriteError

  // Only fetch individual images for client-side stitching if server sprite isn't available
  const needsClientStitching =
    panAndZoom && !serverSpriteAvailable && !isValidatingServerSprite
  const warmedLargeImageUrls = useMemo(
    () =>
      needsClientStitching
        ? (wellObservation?.montage?.largeImages?.map(image =>
            warmImageURL(image.imageUrl),
          ) ?? [])
        : [],
    [wellObservation?.montage?.largeImages, needsClientStitching],
  )

  const {
    data: extractedImages,
    isLoading: isExtracting,
    error: extractionError,
  } = useLargeImageBlobUrls(warmedLargeImageUrls, needsClientStitching)

  const {
    data: clientStitchedImage,
    isLoading: isStitching,
    error: stitchingError,
  } = useStitchedMontageImage(
    extractedImages,
    montageRows,
    montageColumns,
    warmedLargeImageUrls,
    extractedImages &&
      extractedImages.length > 0 &&
      !isExtracting &&
      needsClientStitching,
  )

  // Use server-stitched image if available, otherwise fall back to client-stitched
  const stitchedImage = validatedServerSpriteUrl ?? clientStitchedImage

  // Calculate overall loading and error states
  const isLoading =
    isValidatingServerSprite || (needsClientStitching && (isExtracting || isStitching))
  const hasError = needsClientStitching && (extractionError || stitchingError)

  // Notify parent of loading state changes
  useEffect(() => {
    onLoadingStateChange?.(isLoading)
  }, [isLoading, onLoadingStateChange])

  // Measure and report container dimensions
  useEffect(() => {
    if (!containerRef.current || !stitchImage) return

    const measureDimensions = () => {
      const container = containerRef.current
      if (!container) return

      const rect = container.getBoundingClientRect()
      if (rect.width > 0 && rect.height > 0) {
        const dims = { width: rect.width, height: rect.height }
        setViewportDimensions(dims)
        onViewportDimensionsChange?.(dims)
      }
    }

    // Measure immediately
    measureDimensions()

    // Also measure after a short delay to ensure DOM is ready
    const timeoutId = setTimeout(measureDimensions, 100)

    // And after a longer delay as fallback
    const fallbackTimeoutId = setTimeout(measureDimensions, 500)

    // Re-measure on resize
    const resizeObserver = new ResizeObserver(measureDimensions)
    resizeObserver.observe(containerRef.current)

    return () => {
      clearTimeout(timeoutId)
      clearTimeout(fallbackTimeoutId)
      resizeObserver.disconnect()
    }
  }, [onViewportDimensionsChange, stitchImage])

  // Persist zoom/pan state only when gestures finish to avoid per-frame rerenders
  const persistZoomPan = useCallback(
    (ref: ReactZoomPanPinchRef) => {
      if (!onZoomPanChange) return
      const { scale, positionX, positionY } = ref.state || {}
      if (scale == null) return
      onZoomPanChange({ scale, positionX, positionY })
    },
    [onZoomPanChange],
  )

  return (
    <>
      <div
        ref={containerRef}
        className={cs.container}
        style={{
          ['--montage-rows' as string]: props.stitchImage ? 1 : montageRows,
          ['--montage-columns' as string]: props.stitchImage ? 1 : montageColumns,
          ['--num-sites-per-montage' as string]: numSitesInMontageLayout,
          // Use well sprite for grid view - higher quality than plate sprite
          ['--sprite-url-2x' as string]: wellSpriteURL
            ? `url("${wellSpriteURL}")`
            : null,
        }}
      >
        <div className={cs.well} data-montage-export-root>
          <div
            className={cx(cs.layer, cs.imagery, {
              [cs.notImaged]: (!wellSpriteURL || hasError) && !singleImageURL,
              [cs.singleImage]:
                (props.stitchImage && !!stitchedImage) ||
                numSitesInMontageLayout === 1 ||
                (!wellSpriteURL && !!singleImageURL),
              [cs.loading]: isLoading,
            })}
            style={{
              ['--single-image-url' as string]: singleImageURL
                ? `url("${singleImageURL}")`
                : null,
            }}
          >
            {(() => {
              if (props.stitchImage && !stitchedImage) {
                return (
                  <MontageHeroLoadingPlaceholder
                    plateDims={culture.culturePlate.plateDimensions}
                  />
                )
              }

              if (props.stitchImage && stitchedImage) {
                const filterString = generateFilterString(imageFilters)

                return (
                  <TransformWrapper
                    key={`zoom-${props.zoomPanState?.scale ?? 1}-${props.zoomPanState?.positionX ?? 0}-${props.zoomPanState?.positionY ?? 0}`}
                    initialScale={props.zoomPanState?.scale ?? 1}
                    initialPositionX={props.zoomPanState?.positionX ?? 0}
                    initialPositionY={props.zoomPanState?.positionY ?? 0}
                    minScale={1}
                    maxScale={5}
                    wheel={{
                      step: 0.01,
                      smoothStep: 0.01,
                      wheelDisabled: false,
                      touchPadDisabled: false,
                    }}
                    doubleClick={{ step: 1 }}
                    zoomAnimation={{ animationTime: 150 }}
                    centerOnInit={!props.zoomPanState} // Don't center if we have saved state
                    limitToBounds={true}
                    disablePadding={true}
                    onZoomStop={persistZoomPan}
                    onPanningStop={persistZoomPan}
                    onPinchingStop={persistZoomPan}
                  >
                    <div className={cs.transform}>
                      <TransformComponent
                        wrapperClass={cs.transformWrapper}
                        contentClass={cs.transformImage}
                      >
                        <img
                          src={stitchedImage}
                          alt='Montage'
                          crossOrigin='anonymous'
                          decoding='async'
                          className={cs.stitchedImage}
                          style={{
                            filter: filterString || undefined,
                            transition: 'filter 0.2s ease-out',
                          }}
                        />
                      </TransformComponent>
                      <div className={cs.miniMapContainer} data-montage-minimap>
                        <MiniMap width={150} height={100} borderColor='#ccc'>
                          <img
                            src={stitchedImage}
                            alt='Minimap'
                            crossOrigin='anonymous'
                            decoding='async'
                            className={cs.miniMapImage}
                            style={{
                              filter: filterString || undefined,
                            }}
                          />
                        </MiniMap>
                      </div>
                      <button
                        type='button'
                        className={cs.expandButton}
                        onClick={() => setIsExpanded(true)}
                        aria-label='Expand image'
                        title='Expand to fullscreen'
                      >
                        <ExpandIcon className={cs.expandIcon} />
                      </button>
                    </div>
                  </TransformWrapper>
                )
              }

              return new Array(numSitesInMontageLayout).fill(null).map((_, site) => {
                // Calculate row and column from site index for well sprite positioning
                const siteRow = Math.floor(site / montageColumns)
                const siteCol = site % montageColumns
                return (
                  <button
                    key={`${siteRow}-${siteCol}`}
                    type='button'
                    className={cs.site}
                    style={{
                      ['--site-row' as string]: siteRow,
                      ['--site-col' as string]: siteCol,
                    }}
                    onClick={() => props.onClickImage?.(site)}
                  />
                )
              })
            })()}
          </div>
        </div>
      </div>

      <ExpandedMontageModal
        isOpen={isExpanded}
        onClose={() => setIsExpanded(false)}
        largeSpriteUrl={largeSpriteUrl}
        stitchedImage={stitchedImage ?? null}
        imageFilters={imageFilters}
        zoomPanState={props.zoomPanState}
        onZoomPanChange={onZoomPanChange}
        mainViewportDimensions={viewportDimensions ?? undefined}
      />
    </>
  )
}

export function MontageHeroLoadingPlaceholder({
  plateDims,
}: {
  plateDims?: PlateDimensions
}) {
  const [montageRows, montageColumns] =
    MONTAGE_LAYOUT_ROWS_COLS[defaultMontageLayout(plateDims)]
  const numSitesInMontageLayout = montageRows * montageColumns

  return (
    <div
      className={cs.container}
      style={{
        ['--montage-rows' as string]: montageRows,
        ['--montage-columns' as string]: montageColumns,
        ['--num-sites-per-montage' as string]: numSitesInMontageLayout,
      }}
    >
      <div className={cs.well}>
        <div className={cx(cs.layer, cs.imagery, cs.notImaged)}>
          {new Array(numSitesInMontageLayout).fill(null).map((_, site) => {
            const siteRow = Math.floor(site / montageColumns)
            const siteCol = site % montageColumns
            return (
              <div
                key={`${siteRow}-${siteCol}`}
                className={cs.site}
                style={{
                  ['--site-row' as string]: siteRow,
                  ['--site-col' as string]: siteCol,
                }}
              ></div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
